import React, { useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@repo/ui";
import Carousel from "../../../../components/Carousel/Carousel";
import Modal from "../../../../components/Modal/Modal";
import placeholderImage from "../../../../assets/placeholders/picture.webp";
import { FullGiftTableProduct } from "./types";
import { gsap } from "gsap";
import { withTranslations } from "../../../../hoc/withTranslations/withTranslations";

const LOCALE = "modules_Guests_components_TableContent_ProductModal";

interface ProductModalProps {
  isOpen: boolean;
  product: FullGiftTableProduct | null;
  quantity: number;
  modalAlert: string | null;
  onIncrement: () => void;
  onDecrement: () => void;
  onAddToCart: () => void;
  onClose: () => void;
}

const ProductModal: React.FC<ProductModalProps> = ({
  isOpen,
  product,
  quantity,
  modalAlert,
  onIncrement,
  onDecrement,
  onAddToCart,
  onClose,
}) => {
  const { t } = useTranslation(LOCALE);
  const modalAlertRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (modalAlertRef.current) {
      gsap.fromTo(
        modalAlertRef.current,
        { opacity: 0, scale: 0.95 },
        { opacity: 1, scale: 1, duration: 1, ease: "power2.out" }
      );
    }
  }, [modalAlert]);

  if (!product) return null;

  const { api } = product;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="z-50 fixed inset-0 flex items-center justify-center"
    >
      <h2 className="text-center font-bold text-xl">{api.title}</h2>
      <div className="relative mt-4">
        {api.pictures?.length > 0 ? (
          <Carousel>
            {api.pictures.map((picture: string, index: number) => (
              <img
                key={index}
                src={picture}
                alt={`${t("slide")} ${index}`}
                className="w-full h-96 object-contain"
              />
            ))}
          </Carousel>
        ) : (
          <img
            src={placeholderImage}
            alt={t("noImage")}
            className="w-full h-96 object-contain"
          />
        )}
      </div>
      <div className="w-full border p-4 mt-8">
        <div className="flex justify-between items-center">
          <span>{t("quantity")}</span>
          <div className="flex items-center">
            <button
              onClick={onDecrement}
              className="px-4 py-2 text-lg bg-gray-200 rounded-md hover:bg-gray-300"
            >
              -
            </button>
            <span className="mx-4 font-bold">{quantity}</span>
            <button
              onClick={onIncrement}
              className="px-4 py-2 text-lg bg-gray-200 rounded-md hover:bg-gray-300"
            >
              +
            </button>
          </div>
        </div>
        {modalAlert && (
          <div ref={modalAlertRef} className="mt-4 text-center text-primary">
            <p className="font-bold">{modalAlert}</p>
          </div>
        )}
        <Button
          label={t("addToCart")}
          onClick={onAddToCart}
          className="mt-4 w-full bg-primary text-white"
        />
        <Button
          label={t("cancel")}
          type="secondary"
          onClick={onClose}
          className="mt-2 w-full"
        />
      </div>
    </Modal>
  );
};

export default withTranslations(LOCALE)(ProductModal);
